/**
 * Animation helper functions
 * Utilities for calculating animation transforms and progress
 */

import { normalizeTransformOrigin, getKeyframeValue } from './utils'

export interface TransformCalculationContext {
  animationConfig: any
  parentHeight: number
  parentWidth: number
  cachedParentHeight: number
  cachedParentWidth: number
  needsRecalc: boolean
  parentContainer: HTMLElement
}

export interface TransformProps {
  y?: number
  x?: number
  scale?: number
  rotation?: number
}

export interface InitialTransformProps {
  y?: number
  x?: number
  scale?: number
  rotation?: number
}

/**
 * Calculate initial transform properties for an animation element
 */
export function calculateInitialTransformProps(
  animationConfig: any,
  parentHeight: number,
  parentWidth: number
): {
  transformProps: TransformProps
  initialTransformProps: InitialTransformProps
} {
  const transformProps: TransformProps = {}
  const initialTransformProps: InitialTransformProps = {}

  // Handle vertical position animations (convert to translateY)
  if (animationConfig.position.top && typeof animationConfig.position.top === 'object') {
    const fromPercent = animationConfig.position.top.from
    const toPercent = animationConfig.position.top.to
    const fromPixels = (parentHeight * fromPercent) / 100
    const toPixels = (parentHeight * toPercent) / 100
    // Transform starts at 0, ends at the difference
    initialTransformProps.y = 0
    transformProps.y = toPixels - fromPixels
  }

  if (animationConfig.position.bottom && typeof animationConfig.position.bottom === 'object') {
    const fromPercent = animationConfig.position.bottom.from
    const toPercent = animationConfig.position.bottom.to
    const fromPixels = (parentHeight * fromPercent) / 100
    const toPixels = (parentHeight * toPercent) / 100
    // For bottom, negative values move up
    initialTransformProps.y = 0
    transformProps.y = -(toPixels - fromPixels)
  }

  // Handle horizontal position animations (convert to translateX)
  if (animationConfig.position.left && typeof animationConfig.position.left === 'object') {
    const fromPercent = animationConfig.position.left.from
    const toPercent = animationConfig.position.left.to
    const fromPixels = (parentWidth * fromPercent) / 100
    const toPixels = (parentWidth * toPercent) / 100
    initialTransformProps.x = 0
    transformProps.x = toPixels - fromPixels
  }

  if (animationConfig.position.right && typeof animationConfig.position.right === 'object') {
    const fromPercent = animationConfig.position.right.from
    const toPercent = animationConfig.position.right.to
    const fromPixels = (parentWidth * fromPercent) / 100
    const toPixels = (parentWidth * toPercent) / 100
    // For right, negative values move left
    initialTransformProps.x = 0
    transformProps.x = -(toPixels - fromPixels)
  }

  // Handle scale animation (from/to or keyframes)
  const keyframes = animationConfig.keyframes?.slice().sort((a: any, b: any) => a.at - b.at)
  const scaleFromKeyframes = keyframes?.some((k: any) => k.scale !== undefined)
  if (scaleFromKeyframes && keyframes?.length) {
    const first = keyframes.find((k: any) => k.scale !== undefined) ?? keyframes[0]
    initialTransformProps.scale = first.scale ?? initialTransformProps.scale
    if (animationConfig.transformOrigin) {
      // Will be set later
    }
  } else if (animationConfig.scale && typeof animationConfig.scale === 'object') {
    initialTransformProps.scale = animationConfig.scale.from
    transformProps.scale = animationConfig.scale.to
    if (animationConfig.transformOrigin) {
      // Will be set later
    }
  }

  // Handle rotate animation (from/to or keyframes)
  const rotateFromKeyframes = keyframes?.some((k: any) => k.rotate !== undefined)
  if (rotateFromKeyframes && keyframes?.length) {
    const first = keyframes.find((k: any) => k.rotate !== undefined) ?? keyframes[0]
    initialTransformProps.rotation = first.rotate ?? initialTransformProps.rotation
    if (animationConfig.transformOrigin) {
      // Will be set later
    }
  } else if (animationConfig.rotate) {
    initialTransformProps.rotation = animationConfig.rotate.from
    transformProps.rotation = animationConfig.rotate.to
    if (animationConfig.transformOrigin) {
      // Will be set later
    }
  }

  return { transformProps, initialTransformProps, keyframes }
}

/**
 * Calculate animated transform based on progress
 */
export function calculateAnimatedTransform(
  progress: number,
  progressPercent: number,
  context: TransformCalculationContext,
  transformProps: TransformProps,
  initialTransformProps: InitialTransformProps,
  keyframes: any[] | undefined,
  scaleFromKeyframes: boolean,
  rotateFromKeyframes: boolean,
  defaultKeyframeEase?: string
): TransformProps {
  const animatedTransform: TransformProps = {}

  // Use cached parent dimensions (only recalculate if needed)
  let cachedParentHeight = context.cachedParentHeight
  let cachedParentWidth = context.cachedParentWidth
  if (context.needsRecalc) {
    const currentParentRect = context.parentContainer.getBoundingClientRect()
    cachedParentHeight = currentParentRect.height
    cachedParentWidth = currentParentRect.width
  }

  // Calculate transform values based on progress
  if (transformProps.y !== undefined) {
    // Recalculate y transform if parent size changed
    if (context.animationConfig.position.top && typeof context.animationConfig.position.top === 'object') {
      const fromPercent = context.animationConfig.position.top.from
      const toPercent = context.animationConfig.position.top.to
      const fromPixels = (cachedParentHeight * fromPercent) / 100
      const toPixels = (cachedParentHeight * toPercent) / 100
      const targetY = toPixels - fromPixels
      animatedTransform.y = targetY * progress
    } else if (context.animationConfig.position.bottom && typeof context.animationConfig.position.bottom === 'object') {
      const fromPercent = context.animationConfig.position.bottom.from
      const toPercent = context.animationConfig.position.bottom.to
      const fromPixels = (cachedParentHeight * fromPercent) / 100
      const toPixels = (cachedParentHeight * toPercent) / 100
      const targetY = -(toPixels - fromPixels)
      animatedTransform.y = targetY * progress
    }
  }

  if (transformProps.x !== undefined) {
    // Recalculate x transform if parent size changed
    if (context.animationConfig.position.left && typeof context.animationConfig.position.left === 'object') {
      const fromPercent = context.animationConfig.position.left.from
      const toPercent = context.animationConfig.position.left.to
      const fromPixels = (cachedParentWidth * fromPercent) / 100
      const toPixels = (cachedParentWidth * toPercent) / 100
      const targetX = toPixels - fromPixels
      animatedTransform.x = targetX * progress
    } else if (context.animationConfig.position.right && typeof context.animationConfig.position.right === 'object') {
      const fromPercent = context.animationConfig.position.right.from
      const toPercent = context.animationConfig.position.right.to
      const fromPixels = (cachedParentWidth * fromPercent) / 100
      const toPixels = (cachedParentWidth * toPercent) / 100
      const targetX = -(toPixels - fromPixels)
      animatedTransform.x = targetX * progress
    }
  }

  // Keyframe-based translateY / translateX (values are multipliers of parent height/width)
  const translateYFromKeyframes = keyframes?.some((k: any) => k.translateY !== undefined)
  const translateXFromKeyframes = keyframes?.some((k: any) => k.translateX !== undefined)
  if (keyframes && translateYFromKeyframes) {
    const v = getKeyframeValue(keyframes, progressPercent, 'translateY', defaultKeyframeEase as any)
    if (v !== undefined) animatedTransform.y = cachedParentHeight * v
  }
  if (keyframes && translateXFromKeyframes) {
    const v = getKeyframeValue(keyframes, progressPercent, 'translateX', defaultKeyframeEase as any)
    if (v !== undefined) animatedTransform.x = cachedParentWidth * v
  }

  if (keyframes && scaleFromKeyframes) {
    const v = getKeyframeValue(keyframes, progressPercent, 'scale', defaultKeyframeEase as any)
    if (v !== undefined) animatedTransform.scale = v
  } else if (transformProps.scale !== undefined) {
    animatedTransform.scale =
      initialTransformProps.scale! + (transformProps.scale - initialTransformProps.scale!) * progress
  }

  if (keyframes && rotateFromKeyframes) {
    const v = getKeyframeValue(keyframes, progressPercent, 'rotate', defaultKeyframeEase as any)
    if (v !== undefined) animatedTransform.rotation = v
  } else if (transformProps.rotation !== undefined) {
    animatedTransform.rotation =
      initialTransformProps.rotation! + (transformProps.rotation - initialTransformProps.rotation!) * progress
  }

  return animatedTransform
}
